import { useState } from "react";
import {
  LayoutDashboard,
  Network,
  Sparkles,
  CreditCard,
  Building2,
  HelpCircle,
  ChevronRight,
  Menu,
  X,
  BookOpen
} from "lucide-react";

const navItems = [
  { id: "hero", label: "Dashboard", icon: LayoutDashboard },
  { id: "features", label: "Network", icon: Network },
  { id: "ai-demo", label: "AI Demo", icon: Sparkles },
  { id: "pricing", label: "Pricing", icon: CreditCard },
  { id: "about", label: "Company", icon: Building2 },
  { id: "blog", label: "Blog", icon: BookOpen },
  { id: "faq", label: "Support", icon: HelpCircle },
];

export default function Sidebar({ activeSection, onNavigate }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleNav = (id) => {
    onNavigate(id);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile toggle */}
      <button
        data-testid="sidebar-mobile-toggle"
        onClick={() => setMobileOpen(!mobileOpen)}
        className="fixed top-4 left-4 z-50 md:hidden bg-[#0D0D0D] text-white p-2.5 rounded-xl shadow-lg active:scale-95 transition-transform"
        aria-label={mobileOpen ? "Close menu" : "Open menu"}
      >
        {mobileOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`
          fixed md:relative z-40 w-[220px] flex-shrink-0 bg-[#0D0D0D] text-white 
          flex flex-col h-full transition-transform duration-300 ease-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
      >
        {/* Logo */}
        <div className="px-5 pt-7 pb-8">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg cta-gradient flex items-center justify-center">
              <Network size={16} className="text-white" />
            </div>
            <span className="text-lg font-semibold tracking-tight">Relio</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = activeSection === item.id;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => handleNav(item.id)}
                className={`
                  w-full flex items-center gap-3 px-3 h-9 rounded-lg text-sm font-medium
                  transition-all duration-200 relative group
                  ${isActive
                    ? "bg-white/10 text-white"
                    : "text-white/50 hover:text-white/80 hover:bg-white/5"
                  }
                `}
              >
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-[#a33900] rounded-r" />
                )}
                <Icon size={16} strokeWidth={1.5} />
                <span>{item.label}</span>
                {isActive && (
                  <ChevronRight size={14} className="ml-auto opacity-50" />
                )}
              </button>
            );
          })}
        </nav>

        {/* User placeholder */}
        <div className="px-4 pb-5 mt-auto">
          <div className="flex items-center gap-3 px-2 py-3 rounded-xl bg-white/5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#a33900] to-[#cc4900] flex items-center justify-center text-xs font-semibold">
              JD
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">Jane Doe</p>
              <p className="text-[10px] text-white/40 truncate">Founder & CEO</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {mobileOpen && (
        <div
          data-testid="sidebar-mobile-overlay"
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 md:hidden transition-opacity duration-300"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </>
  );
}
